import { createMemory, searchMemories } from '@4cm/brain/brainMemory';
import { isPlaceholderSupabase } from './supabase';
import { appendLocalPlayerGoal, loadLocalPlayerGoals } from './demoData';

/**
 * Player goal flow (Layer 3, scope='player').
 *
 * Two short questions per player. Saves as a brain_memory scoped to the
 * player so Brain retrieves it whenever context.kind='player' or the
 * player_id is in the screen context. Closes the Layer 2 → Layer 3 loop:
 * the next Brain turn that sees this player gets the goal as a prior.
 */

export interface PlayerGoalQuestion {
  id: string;
  prompt: string;
  hint: string;
}

export const PLAYER_GOAL_QUESTIONS: PlayerGoalQuestion[] = [
  {
    id: 'focus',
    prompt: 'What\'s the one thing you want this player to be better at by the end of the next four weeks?',
    hint: 'specific is better than broad — "first touch under pressure" beats "technical"',
  },
  {
    id: 'evidence',
    prompt: 'How will you know they\'ve got it? What does the evidence look like?',
    hint: 'a moment in a game, a drill they nail, a teammate noticing — anything observable',
  },
];

export interface PlayerGoalAnswer {
  questionId: string;
  prompt: string;
  answer: string;
}

export interface PlayerGoalState {
  step: number;
  answers: PlayerGoalAnswer[];
  finished: boolean;
}

export function createInitialPlayerGoal(): PlayerGoalState {
  return { step: 0, answers: [], finished: false };
}

export function advancePlayerGoal(state: PlayerGoalState, answer: string): PlayerGoalState {
  const clean = answer.trim();
  if (!clean) return state;
  const question = PLAYER_GOAL_QUESTIONS[state.step];
  if (!question) return state;
  const nextAnswers = [...state.answers, { questionId: question.id, prompt: question.prompt, answer: clean }];
  const nextStep = state.step + 1;
  return {
    step: nextStep,
    answers: nextAnswers,
    finished: nextStep >= PLAYER_GOAL_QUESTIONS.length,
  };
}

export function currentPlayerGoalQuestion(state: PlayerGoalState): PlayerGoalQuestion | null {
  return PLAYER_GOAL_QUESTIONS[state.step] ?? null;
}

export interface PlayerGoalSummary {
  id: string;
  fact: string;
  created_at: string;
}

interface SaveOptions {
  coachId: string;
  teamId: string | null;
  playerId: string;
  playerName: string | null;
  answers: PlayerGoalAnswer[];
}

export async function savePlayerGoal(opts: SaveOptions): Promise<{ ok: true; memoryId: string } | { ok: false; error: string }> {
  const { coachId, teamId, playerId, playerName, answers } = opts;
  const focus = answers.find((a) => a.questionId === 'focus')?.answer ?? '';
  const evidence = answers.find((a) => a.questionId === 'evidence')?.answer ?? '';

  const fact = [
    `Goal for ${playerName ?? 'player'} (${new Date().toLocaleDateString()}):`,
    `• Focus: ${focus}`,
    `• Evidence of progress: ${evidence}`,
  ].join('\n');
  const tags = ['player-goal', 'layer3', 'four-week'];

  // Offline path — Brain memory writes can't reach Supabase, so persist
  // locally in the same shape we'd read back via listPlayerGoals.
  if (isPlaceholderSupabase()) {
    try {
      const row = appendLocalPlayerGoal({
        coach_id: coachId,
        player_id: playerId,
        player_name: playerName,
        fact,
        tags,
      });
      return { ok: true, memoryId: row.id };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Could not save goal locally.' };
    }
  }

  const memory = await createMemory({
    coach_id: coachId,
    team_id: teamId,
    scope: 'player',
    entity_id: playerId,
    kind: 'reflection',
    fact,
    tags,
    confidence: 0.9,
  });

  if (!memory) return { ok: false, error: 'Could not save goal.' };
  return { ok: true, memoryId: memory.id };
}

export async function listPlayerGoals(coachId: string, playerId: string, limit = 6): Promise<PlayerGoalSummary[]> {
  if (isPlaceholderSupabase()) {
    return loadLocalPlayerGoals(coachId, playerId)
      .slice(0, limit)
      .map((g) => ({ id: g.id, fact: g.fact, created_at: g.created_at }));
  }

  const memories = await searchMemories({
    coach_id: coachId,
    scope: 'player',
    entity_id: playerId,
    tags: ['player-goal'],
    limit,
  });
  return memories.map((m) => ({
    id: m.id,
    fact: m.fact,
    created_at: m.created_at,
  }));
}
